import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import {
  BillingProfileDTO,
  BillingProfileReference,
  BillingDocumentType,
  GstRegistration,
  documentTypeLabels,
  gstRegistrationLabels,
  BILLING_CURRENCIES,
} from '../../types/billing';
import {
  INDIA,
  ISSUER_FIELDS,
  clientErrors,
  hasIssuerFields,
  isIndia,
  panFromGstin,
  registrationOptions,
  showsGstin,
  toSaveBody,
  withCountry,
} from './billingProfileRules';
import { formatServerUtcAsIst } from './closeBooksRules';

interface Props {
  organizationId: string;
  /** Shown while the profile loads. */
  organizationName?: string;
  /** False: the form is shown read-only (the caller cannot save it). */
  editable: boolean;
  onClose: () => void;
  /** Called after a save; without it the form stays open and says it saved. */
  onSaved?: (message: string) => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const hintCls = 'text-xs text-gray-500 dark:text-gray-400 mt-1';
const sectionCls = 'border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4';
const sectionTitleCls = 'text-sm font-semibold text-gray-900 dark:text-white';

const FALLBACK_REFERENCE: BillingProfileReference = {
  states: [],
  countries: [{ code: INDIA, name: 'India' }],
  currencies: [...BILLING_CURRENCIES],
  gstRegistrations: ['REGULAR', 'COMPOSITION', 'SEZ_UNIT', 'SEZ_DEVELOPER', 'UNREGISTERED', 'OVERSEAS'],
  documentTypes: ['TAX_INVOICE', 'INTERNAL_STATEMENT'],
};

/**
 * One organisation's billing profile (backend docs/BILLING.md §5, phase 1): who it is
 * on an invoice. Edited by whoever bills it — the organisation above it — or the
 * platform; everyone else sees it read-only. A profile nobody has saved yet arrives
 * as a draft prefilled from the credit account (configured=false).
 */
const BillingProfileModal: React.FC<Props> = ({ organizationId, organizationName, editable, onClose, onSaved }) => {
  const [ref, setRef] = useState<BillingProfileReference>(FALLBACK_REFERENCE);
  const [profile, setProfile] = useState<BillingProfileDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [issuerOpen, setIssuerOpen] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([billingService.getBillingProfileReference(), billingService.getBillingProfile(organizationId)]).then(
      ([r, p]) => {
        if (!live) return;
        if (r.success && r.data) setRef(r.data);
        if (p.success && p.data) {
          setProfile(p.data);
          setIssuerOpen(hasIssuerFields(p.data));
          setLoadError(null);
        } else {
          setLoadError(p.error || 'Failed to load the billing profile');
        }
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [organizationId]);

  const readOnly = !editable;
  const name = profile?.organizationName || organizationName || organizationId;

  const set = (patch: Partial<BillingProfileDTO>) => {
    setProfile((p) => (p ? { ...p, ...patch } : p));
    setSaved(null);
    // A field's old error no longer describes it once it is edited.
    const keys = Object.keys(patch);
    setErrors((e) => {
      if (!keys.some((k) => k in e)) return e;
      const next = { ...e };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || readOnly) return;
    setSaveError(null);
    setSaved(null);
    const local = clientErrors(profile);
    if (Object.keys(local).length) {
      setErrors(local);
      setSaveError('Fix the highlighted fields.');
      return;
    }
    setSaving(true);
    const r = await billingService.saveBillingProfile(organizationId, toSaveBody(profile));
    setSaving(false);
    if (r.success && r.data) {
      setProfile(r.data);
      setErrors({});
      const message = `Billing profile saved for ${r.data.organizationName || name}.`;
      if (onSaved) onSaved(message);
      else setSaved(message);
      return;
    }
    // Keep the form as typed: on a 403 the user may copy it out, on a 400 they fix it.
    const fieldErrors = r.fieldErrors || {};
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).some((k) => ISSUER_FIELDS.includes(k))) setIssuerOpen(true);
    setSaveError(r.error || 'Failed to save the billing profile');
  };

  const fieldError = (field: string) =>
    errors[field] ? <p className="text-xs text-red-600 mt-1">{errors[field]}</p> : null;
  const inputClass = (field: string) => `${inputCls}${errors[field] ? ' border-red-500 dark:border-red-500' : ''}`;

  const text = (
    field: keyof BillingProfileDTO,
    label: string,
    opts: { required?: boolean; placeholder?: string; type?: string; hint?: string; upper?: boolean; maxLength?: number } = {},
  ) => (
    <div>
      <label className={labelCls} htmlFor={`bp-${field}`}>
        {label}
        {opts.required ? ' *' : ''}
      </label>
      <input
        id={`bp-${field}`}
        type={opts.type || 'text'}
        value={((profile as any)?.[field] as string) || ''}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
        disabled={readOnly}
        onChange={(ev) => set({ [field]: opts.upper ? ev.target.value.toUpperCase() : ev.target.value } as any)}
        className={inputClass(field)}
      />
      {opts.hint && <p className={hintCls}>{opts.hint}</p>}
      {fieldError(field)}
    </div>
  );

  const india = profile ? isIndia(profile) : true;
  const registered = profile ? showsGstin(profile) : true;
  const derivedPan = profile ? panFromGstin(profile.gstin) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] flex flex-col mx-4">
        <div className="mb-4">
          <h2 className="text-xl font-semibold dark:text-white">Billing profile · {name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Who this organisation is on an invoice.
            {readOnly ? ' Read-only: it is set by the organisation that bills it.' : ''}
            {profile?.configured && profile.updatedAt
              ? ` Last saved ${formatServerUtcAsIst(profile.updatedAt)} IST${profile.updatedBy ? ` by ${profile.updatedBy}` : ''}.`
              : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : loadError || !profile ? (
          <>
            <p className="text-sm text-red-600">{loadError}</p>
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={save} className="flex flex-col min-h-0" noValidate>
            <div className="overflow-y-auto space-y-4 pr-1">
              {!profile.configured && (
                <div className="rounded-lg border p-3 text-sm bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300">
                  Not saved yet — prefilled from the account. Review and save.
                </div>
              )}

              {/* ── Legal identity & address ── */}
              <fieldset className={sectionCls}>
                <legend className={`${sectionTitleCls} px-1`}>Legal identity &amp; address</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {text('legalName', 'Legal name', { required: true, hint: 'As registered (on the GST certificate in India).' })}
                  {text('tradeName', 'Trade name')}
                </div>
                {text('addressLine1', 'Address line 1', { required: true })}
                {text('addressLine2', 'Address line 2')}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {text('city', 'City', { required: true })}
                  <div>
                    <label className={labelCls} htmlFor="bp-countryCode">Country *</label>
                    <select
                      id="bp-countryCode"
                      value={profile.countryCode || INDIA}
                      disabled={readOnly}
                      onChange={(ev) => {
                        setProfile((p) => (p ? withCountry(p, ev.target.value) : p));
                        setErrors({});
                      }}
                      className={inputClass('countryCode')}
                    >
                      {!ref.countries.some((c) => c.code === (profile.countryCode || INDIA)) && (
                        <option value={profile.countryCode || INDIA}>{profile.countryCode || INDIA}</option>
                      )}
                      {ref.countries.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                    {fieldError('countryCode')}
                  </div>
                  {india ? (
                    <div>
                      <label className={labelCls} htmlFor="bp-stateCode">State *</label>
                      <select
                        id="bp-stateCode"
                        value={profile.stateCode || ''}
                        disabled={readOnly}
                        onChange={(ev) => {
                          const st = ref.states.find((s) => s.code === ev.target.value);
                          set({ stateCode: ev.target.value || null, state: st?.name || null });
                        }}
                        className={inputClass('stateCode')}
                      >
                        <option value="">Choose the state…</option>
                        {ref.states.map((s) => (
                          <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                        ))}
                      </select>
                      {fieldError('stateCode')}
                      {fieldError('state')}
                    </div>
                  ) : (
                    text('state', 'State / province')
                  )}
                  {text('postalCode', india ? 'PIN code' : 'Postal code', {
                    required: true,
                    maxLength: india ? 6 : undefined,
                    placeholder: india ? '560001' : undefined,
                  })}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {text('billingEmails', 'Billing emails', { hint: 'Comma-separated; invoices go to all of them.' })}
                  {text('phone', 'Phone')}
                </div>
              </fieldset>

              {/* ── Tax ── */}
              <fieldset className={sectionCls}>
                <legend className={`${sectionTitleCls} px-1`}>Tax</legend>
                {india ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls} htmlFor="bp-gstRegistration">GST registration *</label>
                      <select
                        id="bp-gstRegistration"
                        value={profile.gstRegistration || ''}
                        disabled={readOnly}
                        onChange={(ev) => {
                          const reg = (ev.target.value || null) as GstRegistration | null;
                          set(reg === 'UNREGISTERED' ? { gstRegistration: reg, gstin: null } : { gstRegistration: reg });
                        }}
                        className={inputClass('gstRegistration')}
                      >
                        <option value="">Choose…</option>
                        {registrationOptions(ref.gstRegistrations).map((g) => (
                          <option key={g} value={g}>{gstRegistrationLabels[g] || g}</option>
                        ))}
                      </select>
                      {fieldError('gstRegistration')}
                    </div>
                    {registered &&
                      text('gstin', 'GSTIN', {
                        required: true,
                        upper: true,
                        maxLength: 15,
                        placeholder: '29ABCDE1234F1Z5',
                        hint: 'Of the billed address: its first two digits are the state code.',
                      })}
                    {registered ? (
                      <div>
                        <label className={labelCls} htmlFor="bp-pan">PAN</label>
                        <input
                          id="bp-pan"
                          type="text"
                          value={derivedPan || profile.pan || ''}
                          disabled
                          className={inputClass('pan')}
                        />
                        <p className={hintCls}>Taken from the GSTIN.</p>
                        {fieldError('pan')}
                      </div>
                    ) : (
                      text('pan', 'PAN', { upper: true, maxLength: 10, placeholder: 'ABCDE1234F' })
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {text('taxId', 'Tax ID (VAT/EIN)')}
                  </div>
                )}
                {!registered && fieldError('gstin')}
              </fieldset>

              {/* ── Invoicing ── */}
              <fieldset className={sectionCls}>
                <legend className={`${sectionTitleCls} px-1`}>Invoicing</legend>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Issued by</label>
                    <input
                      type="text"
                      value={profile.issuedByOrganizationName || profile.issuedByOrganizationId || '—'}
                      disabled
                      className={inputCls}
                    />
                    <p className={hintCls}>The organisation that invoices this one.</p>
                    {fieldError('issuedByOrganizationId')}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bp-documentType">Document</label>
                    <select
                      id="bp-documentType"
                      value={profile.documentType || 'TAX_INVOICE'}
                      disabled={readOnly}
                      onChange={(ev) => set({ documentType: ev.target.value as BillingDocumentType })}
                      className={inputClass('documentType')}
                    >
                      {ref.documentTypes.map((d) => (
                        <option key={d} value={d}>{documentTypeLabels[d] || d}</option>
                      ))}
                    </select>
                    {fieldError('documentType')}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bp-invoiceCurrency">Currency</label>
                    <select
                      id="bp-invoiceCurrency"
                      value={profile.invoiceCurrency || (india ? 'INR' : 'USD')}
                      disabled={readOnly}
                      onChange={(ev) => set({ invoiceCurrency: ev.target.value })}
                      className={inputClass('invoiceCurrency')}
                    >
                      {ref.currencies.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {fieldError('invoiceCurrency')}
                  </div>
                </div>
              </fieldset>

              {/* ── As an issuer ── */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <button
                  type="button"
                  onClick={() => setIssuerOpen((o) => !o)}
                  aria-expanded={issuerOpen}
                  className="w-full flex items-center justify-between gap-2 p-4 text-left"
                >
                  <span>
                    <span className={sectionTitleCls}>As an issuer</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Only needed for an organisation that invoices others (e.g. Green Giraffe, CatchMyCall).
                    </span>
                  </span>
                  {issuerOpen ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </button>
                {issuerOpen && (
                  <div className="px-4 pb-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {text('invoicePrefix', 'Invoice prefix', { upper: true, maxLength: 5, hint: 'GG makes GG/26-27/0001.' })}
                      {text('defaultSac', 'Default SAC', { maxLength: 6, placeholder: '998414' })}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {text('lutArn', 'LUT ARN', { upper: true, maxLength: 15, hint: 'For zero-rated exports.' })}
                      {text('lutValidFrom', 'LUT valid from', { type: 'date' })}
                      {text('lutValidTo', 'LUT valid to', { type: 'date' })}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {text('bankName', 'Bank name')}
                      {text('bankAccountName', 'Account name')}
                      {text('bankAccountNumber', 'Account number')}
                      {text('bankIfsc', 'IFSC', { upper: true, maxLength: 11 })}
                      {text('bankSwift', 'SWIFT', { upper: true, maxLength: 11 })}
                    </div>
                    <div>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!profile.einvoiceEnabled}
                          disabled={readOnly || !india}
                          onChange={(ev) => set({ einvoiceEnabled: ev.target.checked })}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        E-invoicing (IRN) enabled
                      </label>
                      <p className={hintCls}>Needs a GSTIN.</p>
                      {fieldError('einvoiceEnabled')}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {saveError && <p className="text-sm text-red-600 mt-4">{saveError}</p>}
            {saved && <p className="text-sm text-green-700 dark:text-green-400 mt-4">{saved}</p>}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button type="submit" disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save profile'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BillingProfileModal;
